import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, Paper, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import {
  AccountBalance, AttachFile, Close, ContentCopy, Payment, QrCode2, Security, UploadFile,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getManualPaymentOptions, submitManualPayment } from '../../services/integrations/manualPayment';
import { brandPalette as palette } from '../../utils/brandPalette';
import { friendlyError } from '../../utils/userMessages';

const money = (amount, currency = 'EUR') => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(amount || 0)); }
  catch (_) { return `${currency} ${Number(amount || 0).toFixed(2)}`; }
};

const copy = async (value) => {
  try { await navigator.clipboard.writeText(String(value || '')); toast.success('Copied.'); }
  catch (_) { toast.error('Unable to copy.'); }
};

export default function PaymentMethodDialog({
  open, onClose, onOnlinePayment, onManualSubmitted,
  companyId, userId, companyName, userName, email,
  paymentType = 'company_registration', plan, currency = 'EUR', billingCycle = '', periodEnd = '',
}) {
  const [options, setOptions] = useState(null);
  const [method, setMethod] = useState('online');
  const [reference, setReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const amount = Number(plan?.price || 0);
  const activeMethods = useMemo(() => {
    const list = [];
    if (options?.online?.enabled) list.push('online');
    if (options?.upi?.enabled) list.push('upi');
    if (options?.bankTransfer?.enabled) list.push('bank_transfer');
    return list;
  }, [options]);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setError(''); setReference(''); setNotes(''); setProof(null); setBusy(false);
    getManualPaymentOptions().then((next) => {
      if (!live) return;
      setOptions(next);
      const methods = [next?.online?.enabled && 'online', next?.upi?.enabled && 'upi', next?.bankTransfer?.enabled && 'bank_transfer'].filter(Boolean);
      setMethod(methods[0] || 'online');
    }).catch((err) => live && setError(friendlyError(err, 'Unable to load payment methods.')));
    return () => { live = false; };
  }, [open]);

  const submit = async () => {
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      if (method === 'online') {
        const result = await onOnlinePayment?.();
        if (result?.redirected) return;
        return;
      }
      const result = await submitManualPayment({
        companyId, userId, paymentType,
        packageId: plan?.id || '', packageName: plan?.name || '', amount, currency: plan?.currency || currency,
        billingCycle: plan?.billingCycle || billingCycle, periodEnd,
        paymentMethod: method, transactionReference: reference, paymentDate, proofFile: proof, notes,
        companyName, userName, email,
      });
      toast.success('Payment proof submitted for Super Admin verification.');
      onManualSubmitted?.(result);
    } catch (err) {
      const message = friendlyError(err, 'Unable to submit payment.');
      setError(message); toast.error(message);
    } finally { setBusy(false); }
  };

  const manual = method === 'upi' || method === 'bank_transfer';
  const details = method === 'upi' ? options?.upi : options?.bankTransfer;

  return (
    <Dialog open={open} onClose={() => !busy && onClose?.()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: `1px solid ${palette.hairline}` }}>
        <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center' }}><Box sx={{ width: 38, height: 38, borderRadius: '12px', display: 'grid', placeItems: 'center', color: '#fff', bgcolor: palette.rust }}><Payment /></Box><Box><Typography sx={{ fontWeight: 900 }}>Choose payment method</Typography><Typography sx={{ fontSize: 12, color: palette.inkLight }}>{plan?.name || 'TELCOFAIR payment'} · {money(amount, plan?.currency || currency)}</Typography></Box></Stack>
        <Button onClick={() => !busy && onClose?.()} sx={{ minWidth: 34, width: 34, height: 34, borderRadius: '50%' }}><Close fontSize="small" /></Button>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {!options && !error && <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {options && !activeMethods.length && <Alert severity="warning">No payment method is currently enabled. Contact TELCOFAIR support.</Alert>}
        {options && activeMethods.length > 0 && <>
          <RadioGroup value={method} onChange={(e) => setMethod(e.target.value)} sx={{ mb: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {options.online?.enabled && <Paper variant="outlined" sx={{ flex: 1, px: 1.2, borderRadius: '12px' }}><FormControlLabel value="online" control={<Radio size="small" />} label={`Online (${String(options.online.provider || 'gateway').toUpperCase()})`} /></Paper>}
              {options.upi?.enabled && <Paper variant="outlined" sx={{ flex: 1, px: 1.2, borderRadius: '12px' }}><FormControlLabel value="upi" control={<Radio size="small" />} label="UPI" /></Paper>}
              {options.bankTransfer?.enabled && <Paper variant="outlined" sx={{ flex: 1, px: 1.2, borderRadius: '12px' }}><FormControlLabel value="bank_transfer" control={<Radio size="small" />} label="Bank Transfer" /></Paper>}
            </Stack>
          </RadioGroup>

          {method === 'online' && <Alert icon={<Security />} severity="info">You will continue through the existing secure {String(options.online.provider || 'payment')} checkout. Existing online-payment logic is unchanged.</Alert>}

          {manual && <Stack spacing={1.3}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '14px', bgcolor: '#FAFCFD' }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}><Box sx={{ color: palette.rust }}>{method === 'upi' ? <QrCode2 /> : <AccountBalance />}</Box><Typography sx={{ fontWeight: 900 }}>{method === 'upi' ? 'UPI payment details' : 'Bank transfer details'}</Typography></Stack>
              {method === 'upi' ? <Stack spacing={.65}>
                <Typography sx={{ fontSize: 13 }}><b>UPI ID:</b> {details?.upiId || 'Not configured'} <Button size="small" onClick={() => copy(details?.upiId)} startIcon={<ContentCopy fontSize="small" />}>Copy</Button></Typography>
                <Typography sx={{ fontSize: 13 }}><b>Payee:</b> {details?.payeeName || 'TELCOFAIR'}</Typography>
                {details?.qrUrl && <Box component="img" src={details.qrUrl} alt="UPI QR" sx={{ width: 180, maxWidth: '100%', borderRadius: '12px', border: `1px solid ${palette.hairline}`, p: .6, bgcolor: '#fff' }} />}
              </Stack> : <Stack spacing={.5}>
                {[['Account holder', details?.accountHolder], ['Bank', details?.bankName], ['Account number', details?.accountNumber], ['IFSC', details?.ifsc], ['Branch', details?.branch], ['SWIFT/BIC', details?.swift], ['IBAN', details?.iban]].filter(([, v]) => v).map(([label, value]) => <Typography key={label} sx={{ fontSize: 13 }}><b>{label}:</b> {value}</Typography>)}
              </Stack>}
              {details?.instructions && <><Divider sx={{ my: 1 }} /><Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 12.5, color: palette.inkLight }}>{details.instructions}</Typography></>}
            </Paper>
            <Alert severity="warning">Pay exactly <b>{money(amount, plan?.currency || currency)}</b>. Your subscription activates only after Super Admin verifies the reference and uploaded proof.</Alert>
            <TextField fullWidth size="small" label="UTR / transaction / reference number" value={reference} onChange={(e) => setReference(e.target.value)} required />
            <TextField fullWidth size="small" type="date" label="Payment date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button component="label" variant="outlined" startIcon={proof ? <AttachFile /> : <UploadFile />} sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800 }}>
              {proof ? proof.name : 'Upload screenshot / bank receipt'}
              <input hidden type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={(e) => setProof(e.target.files?.[0] || null)} />
            </Button>
            <TextField fullWidth size="small" multiline minRows={2} label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Stack>}
        </>}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${palette.hairline}` }}>
        <Button onClick={() => !busy && onClose?.()} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !options || !activeMethods.length || (manual && (!reference.trim() || !proof))} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Payment />} sx={{ textTransform: 'none', fontWeight: 850 }}>
          {busy ? 'Processing…' : method === 'online' ? 'Continue to secure payment' : 'Submit for verification'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
